import { useEffect, useRef, useState } from "react";
import {
  PRESENCE_ACCENT_IDS,
  PRESENCE_ACCENTS,
  persistPresenceTheme,
  readPresenceAccent,
  readPresenceTheme,
  type PresenceAccentId,
  type PresenceTheme,
} from "../shell/theme";

export default function ThemeMenu() {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState<PresenceTheme>(readPresenceTheme);
  const [accent, setAccent] = useState<PresenceAccentId>(readPresenceAccent);
  const box = useRef<HTMLDivElement>(null);
  const current = PRESENCE_ACCENTS[accent];

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function chooseTheme(next: PresenceTheme) {
    setTheme(next);
    persistPresenceTheme(next, accent);
  }

  function chooseAccent(next: PresenceAccentId) {
    setAccent(next);
    persistPresenceTheme(theme, next);
  }

  return (
    <div className="pr-theme" ref={box}>
      <button
        type="button"
        className="pr-iconbtn"
        aria-expanded={open}
        aria-label={theme === "plus" ? "Thème — Aurélia+" : `Thème — ${theme === "dark" ? "sombre" : "clair"}, ${current.label}`}
        title="Thème"
        onClick={() => setOpen((value) => !value)}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      </button>
      {open ? (
        <div className="pr-theme__panel" role="dialog" aria-label="Thème">
          <p className="pr-theme__kicker">Apparence</p>
          <div className="pr-theme__switch">
            <button type="button" className={theme === "light" ? "is-on" : ""} onClick={() => chooseTheme("light")}>
              Clair
            </button>
            <button type="button" className={theme === "dark" ? "is-on" : ""} onClick={() => chooseTheme("dark")}>
              Sombre
            </button>
            <button type="button" className={`pr-theme__plus${theme === "plus" ? " is-on" : ""}`} onClick={() => chooseTheme("plus")}>
              Aurélia+
            </button>
          </div>
          {theme === "plus" ? (
            <p className="pr-theme__label">Bleu néon → violet électrique</p>
          ) : (
            <>
              <p className="pr-theme__kicker">Couleur</p>
              <div className="pr-theme__swatches" role="listbox" aria-label="Couleur d’accent">
                {PRESENCE_ACCENT_IDS.map((id) => (
                  <button
                    key={id}
                    type="button"
                    role="option"
                    aria-selected={accent === id}
                    title={PRESENCE_ACCENTS[id].label}
                    className={accent === id ? "is-on" : ""}
                    style={{ background: PRESENCE_ACCENTS[id].swatch }}
                    onClick={() => chooseAccent(id)}
                  />
                ))}
              </div>
              <p className="pr-theme__label">{current.label}</p>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
